import React, { useEffect, useState, useCallback } from 'react'
import { Box, Typography, Backdrop, CircularProgress } from '@mui/material'
import { TableUrlPagination } from '../../../../ui'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { useApplicationUserPermitsContext } from '../../../../pages/applications/contexts'
import { useApplicationsCalls } from '../../../applications/hooks'
import { applicationDataPreparation } from '../../../applications/logics'

const getLookup = (combo) => {
  let lookup = {}
  combo.forEach((item) => (lookup[item.key] = item.value))
  return lookup
}

export const TableSameCidi = ({ id, cidi }) => {
  const intl = useIntl()
  const { formatMessage } = intl
  const { columns } = useApplicationUserPermitsContext()
  const combos = useSelector((state) => state.combos)
  const navigate = useNavigate()
  const { getApplications } = useApplicationsCalls()

  const [applications, setApplications] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [moreDisclaimer, setMoreDisclaimer] = useState(false)

  useEffect(() => {
    setIsLoading(true)
    getApplications({ cidi })
      .then(({ data }) => {
        const sameCidiApplications = data.solicitudes.filter((application) => application.id !== id)
        setApplications(sameCidiApplications)
        setMoreDisclaimer(data.total_paginas > 1)
        setIsLoading(false)
      })
      .catch(() => {
        setIsLoading(false)
      })
  }, [id, cidi, getApplications])

  const handleGoToDetail = (e, row) => {
    navigate(`/applications/${row.id}`)
  }

  const formattedColumns = useCallback(
    columns
      .filter((column) => column.field !== 'comunicaciones_pendientes')
      .map((column) => {
        const baseColumn = {
          accessorKey: column.field,
          header: formatMessage({ id: `pages.applications.table.columns.${column.field}.title` }),
          size: column.width,
          minSize: column.width,
          maxSize: column.width,
        }

        if (column.comboId && combos[column.comboId]?.data) {
          const lookup = getLookup(combos[column.comboId].data)
          return {
            ...baseColumn,
            Cell: ({ cell }) => {
              const value = cell.getValue()
              return <span>{lookup[value] ?? value ?? '--'}</span>
            },
          }
        }

        return {
          ...baseColumn,
          Cell: ({ cell }) => {
            const value = cell.getValue()
            return <span>{value ?? '--'}</span>
          },
        }
      }),
    [columns, combos, formatMessage]
  )

  const processedData = useCallback(applicationDataPreparation(applications, { intl }, columns), [
    applications,
    columns,
    intl,
  ])

  const tableOptions = {
    toolbar: false,
    selection: false,
    draggable: false,
    paging: false,
  }

  if (isLoading) {
    return (
      <Backdrop open={isLoading} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    )
  }

  return applications.length ? (
    <Box display="flex" flexDirection="column" style={{ width: '100%' }}>
      <TableUrlPagination
        columns={formattedColumns}
        options={tableOptions}
        data={processedData}
        isLoading={isLoading}
        onRowClick={handleGoToDetail}
      />
      {moreDisclaimer ? (
        <span>
          Hay registradas más solicitudes para esa dirección. Por favor, contacta con el gestor de
          servicios si deseas obtenerlas todas
        </span>
      ) : null}
    </Box>
  ) : (
    <Box p={2}>
      <Typography variant="body1">No hay solicitudes en la misma dirección.</Typography>
    </Box>
  )
}

export default TableSameCidi
